import axios from "axios";
import { API } from "../../backend";
import authHeader from "../auth-header";

// Set while the current session is inside a sub-company (a company with no
// login of its own). The parent user's own token is used there, and the
// backend tracks the sub-company in CompanyUser.active_company.
const SUB_COMPANY_FLAG = "active_is_sub_company";

const isInSubCompany = () => localStorage.getItem(SUB_COMPANY_FLAG) === "true";

const clearSwitchBackKeys = () => {
  localStorage.removeItem("original_user");
  localStorage.removeItem("original_companyid");
  localStorage.removeItem("original_company_name");
  localStorage.removeItem("switched_from_company_id");
  localStorage.removeItem("switched_from_company_name");
};

/**
 * Switch company by getting a fresh JWT token for the target company's user.
 * Saves original user to localStorage so we can switch back.
 * Replaces entire user session with the new company's data.
 */
const switchCompany = (company_id) => {
  const wasInSubCompany = isInSubCompany();
  return axios
    .post(API + "company/switch/", { company_id }, { headers: authHeader() })
    .then((response) => {
      if (response.data.success === 1) {
        const d = response.data;

        // Save current session so user can switch back.
        // From inside a sub-company keep the already-saved original session
        // (the user's own company) instead of saving the sub-company session.
        const currentUser = localStorage.getItem("user");
        if (currentUser && !wasInSubCompany) {
          localStorage.setItem("original_user",         currentUser);
          localStorage.setItem("original_companyid",    localStorage.getItem("companyid"));
          localStorage.setItem("original_company_name", localStorage.getItem("active_company_name"));
        }

        // Replace entire localStorage with the switched company's session
        // (same shape as login response — all existing code works unchanged)
        const newUser = {
          access:   d.access,
          refresh:  d.refresh,
          companyid: d.companyid,
          username: d.username,
          locale:   d.locale,
          currency: d.currency,
          parent:   d.parent,
          permissions: d.permissions,
          groups:   d.groups,
          user_id:  d.user_id,
          active_company_id:   d.active_company_id,
          active_company_name: d.active_company_name,
          accessible_companies: d.accessible_companies,
        };

        localStorage.setItem("user",                  JSON.stringify(newUser));
        localStorage.setItem("companyid",             d.companyid);
        localStorage.setItem("username",              JSON.stringify(d.username));
        localStorage.setItem("locale",                d.locale);
        localStorage.setItem("currency",              d.currency);
        localStorage.setItem("parent",                d.parent);
        localStorage.setItem("permissions",           d.permissions);
        localStorage.setItem("userid",                d.user_id);
        localStorage.setItem("active_company_id",     d.active_company_id);
        localStorage.setItem("active_company_name",   d.active_company_name);
        localStorage.setItem("managed_clients_count", d.managed_clients_count);
        localStorage.setItem("accessible_companies",  JSON.stringify(d.accessible_companies));
        // Store switched_from so frontend can show "Switch back to ABC" button
        localStorage.setItem("switched_from_company_id",   d.switched_from_company_id);
        localStorage.setItem("switched_from_company_name", d.switched_from_company_name);

        // ── sub-company bookkeeping ──
        if (d.is_sub_company) {
          localStorage.setItem(SUB_COMPANY_FLAG, "true");
        } else {
          localStorage.removeItem(SUB_COMPANY_FLAG);
          // Left a sub-company straight back to the user's own company:
          // nothing to switch back to any more.
          if (
            wasInSubCompany &&
            String(d.companyid) === String(localStorage.getItem("original_companyid"))
          ) {
            clearSwitchBackKeys();
          }
        }
      }
      return response.data;
    });
};

/**
 * Switch back to the original company using the saved original_user session.
 */
const switchBack = () => {
  // Inside a sub-company the backend must also be told (it clears
  // active_company); then restore the saved session exactly as before.
  if (isInSubCompany()) {
    const originalCompanyId = localStorage.getItem("original_companyid");
    const leave = originalCompanyId
      ? axios
          .post(API + "company/switch/", { company_id: originalCompanyId }, { headers: authHeader() })
          .catch(() => null)
      : Promise.resolve(null);
    return leave.then(() => {
      localStorage.removeItem(SUB_COMPANY_FLAG);
      return restoreOriginalSession();
    });
  }
  return Promise.resolve(restoreOriginalSession());
};

const restoreOriginalSession = () => {
  const originalUser = localStorage.getItem("original_user");
  if (!originalUser) return null;

  const parsed = JSON.parse(originalUser);

  // Restore all original localStorage keys
  localStorage.setItem("user",                 originalUser);
  localStorage.setItem("companyid",            parsed.companyid);
  localStorage.setItem("username",             JSON.stringify(parsed.username));
  localStorage.setItem("locale",               parsed.locale);
  localStorage.setItem("currency",             parsed.currency);
  localStorage.setItem("parent",               parsed.parent);
  localStorage.setItem("permissions",          parsed.permissions);
  localStorage.setItem("userid",               parsed.user_id);
  localStorage.setItem("active_company_id",    parsed.active_company_id  || parsed.companyid);
  localStorage.setItem("active_company_name",  parsed.active_company_name || "");
  localStorage.setItem("accessible_companies", JSON.stringify(parsed.accessible_companies || []));

  // Clear switch-back keys
  clearSwitchBackKeys();

  return parsed;
};

/**
 * Returns true if currently viewing a switched company (not own company).
 */
const isSwitched = () => {
  return !!localStorage.getItem("original_user");
};

/**
 * Get all companies this user can access (own + clients).
 */
const getAccessibleCompanies = () => {
  try {
    return JSON.parse(localStorage.getItem("accessible_companies")) || [];
  } catch {
    return [];
  }
};

/**
 * Get currently active company info from localStorage.
 */
const getActiveCompany = () => {
  return {
    id:   localStorage.getItem("active_company_id"),
    name: localStorage.getItem("active_company_name"),
  };
};

/**
 * Fetch fresh list of clients from API.
 */
const listMyClients = () => {
  return axios.get(API + "company/my-clients/", { headers: authHeader() });
};

const CompanySwitchService = {
  switchCompany,
  switchBack,
  isSwitched,
  isInSubCompany,
  getAccessibleCompanies,
  getActiveCompany,
  listMyClients,
};

export default CompanySwitchService;